import { toast } from 'react-toastify';
import apiInstance from '../../config/HttpCommon';

type DownloadTab = 'assets' | 'pinned' | 'mapview';

type DownloadContext = {
  activeTab: DownloadTab;
  selectedCustomerId: string;
  selectedSiteId: string;
  selectedPlantId: string;
  totalCount: number;
  pinnedTotalCount: number;
  customers: { customerId: string; customerName: string }[];
  sites: { siteId: string; site: string }[];
  plants: unknown;
};

const getDownloadValidationMessage = (context: DownloadContext): string | null => {
  if (!context.selectedCustomerId) {
    return 'Please select a customer to download assets.';
  }

  if (context.activeTab === 'mapview') {
    return 'Download is not available in Map View.';
  }

  if (context.activeTab === 'assets' && context.totalCount <= 0) {
    return 'No assets available to download for the selected filters.';
  }

  if (context.activeTab === 'pinned' && context.pinnedTotalCount <= 0) {
    return 'No pinned assets available to download.';
  }

  return null;
};

export const buildAssetDownloadUrl = (
  customerId: string,
  siteId: string,
  plantId: string
): string => {
  if (customerId && siteId && plantId) {
    return `/Asset/Download/${customerId}/${siteId}/${plantId}`;
  }
  if (customerId && siteId) {
    return `/Asset/Download/${customerId}/${siteId}`;
  }
  return `/Asset/Download/${customerId}`;
};

export const buildAssetDownloadFilename = (context: DownloadContext): string => {
  let filename = 'assets';
  const { selectedCustomerId, selectedSiteId, selectedPlantId, customers, sites, plants } = context;

  if (selectedCustomerId) {
    const customer = customers.find((c) => c.customerId === selectedCustomerId);
    filename += `_${customer?.customerName || selectedCustomerId}`;
  }
  if (selectedSiteId) {
    const site = sites.find((s) => s.siteId === selectedSiteId);
    filename += `_${site?.site || selectedSiteId}`;
  }
  if (selectedPlantId && Array.isArray(plants)) {
    const plant = (plants as { plantId?: string; plantName?: string }[]).find(
      (p) => p.plantId === selectedPlantId
    );
    filename += `_${plant?.plantName || selectedPlantId}`;
  }
  return `${filename}.csv`;
};

const showDownloadErrorToast = (error: {
  response?: { status?: number };
  code?: string;
  message?: string;
}): void => {
  const status = error.response?.status;
  if (status === 404) {
    toast.error('Download file not found. Please contact support.');
    return;
  }
  if (status === 400) {
    toast.error('Invalid request parameters. Please check your selection and try again.');
    return;
  }
  if (status === 401) {
    toast.error('You are not authorized to download this data. Please check your permissions.');
    return;
  }
  if (status === 500) {
    toast.error('Server error occurred while generating the CSV. Please try again later.');
    return;
  }
  if (error.code === 'ECONNABORTED') {
    toast.error('Request timeout. Please try again.');
    return;
  }
  if (error.message === 'Network Error') {
    toast.error('Network error. Please check your connection and try again.');
    return;
  }
  toast.error('An error occurred while downloading the CSV file. Please try again.');
};

export const downloadAssetsCsv = async (context: DownloadContext): Promise<void> => {
  const validationMessage = getDownloadValidationMessage(context);
  if (validationMessage) {
    toast.warning(validationMessage);
    return;
  }

  const apiUrl = buildAssetDownloadUrl(
    context.selectedCustomerId,
    context.selectedSiteId,
    context.selectedPlantId
  );

  try {
    toast.info('Downloading CSV file...', { autoClose: 2000 });

    const response = await apiInstance.get(apiUrl, {
      responseType: 'blob',
      headers: { Accept: 'text/csv' },
    });

    if (response.data.size === 0) {
      toast.error('No assets data available to download.');
      return;
    }

    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildAssetDownloadFilename(context);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    toast.success('CSV file downloaded successfully!');
  } catch (error: unknown) {
    showDownloadErrorToast(error as { response?: { status?: number }; code?: string; message?: string });
  }
};
